<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import IconCheck from '~icons/mdi/check'
import IconClose from '~icons/mdi/close'
import IconMinus from '~icons/mdi/minus'
import IconPlus from '~icons/mdi/plus'
import { maximumNumber, isMultipleOf, minimumNumber } from '~/utils/validation'

interface Props {
  maxQuantity?: number
  multiplier?: number
  variant?: string
  minQuantity?: number
  debounceMs?: number
  size?: 'default' | 'small'
  disableDecrementOnReachMinQuantity?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  multiplier: 1,
  maxQuantity: Number.MAX_SAFE_INTEGER,
  variant: 'outlined',
  debounceMs: 300,
  size: 'default',
  disableDecrementOnReachMinQuantity: false,
})

const quantityValue = defineModel<number>('quantityValue', { default: 0 })
const isEditing = ref(false)
const tempValue = ref(0)
const localValue = ref(quantityValue.value || 0)

// Watch for external changes to model value
watch(() => quantityValue.value, (newVal) => {
  // Only update local value if different to avoid feedback loops
  if (newVal !== localValue.value) {
    localValue.value = newVal || 0
  }
}, { immediate: true })

const isAllowedToSubmit = computed(() => {
  if (props.disableDecrementOnReachMinQuantity && tempValue.value === 0) {
    return false
  }
  return maximumNumber(tempValue.value, props.maxQuantity || null).isValid
    && isMultipleOf(tempValue.value, props.multiplier).isValid
    && minimumNumber(tempValue.value, props.minQuantity || 0).isValid
})
const displayValue = computed(() => localValue.value)
const { t } = useI18n()

// Use the debounce function with the flush option to have access to cancel
const debouncedSetValue = useDebounceFn((newValue: number) => {
  quantityValue.value = newValue
}, props.debounceMs)

// Function to update both local and debounced values
function updateValue(newValue: number) {
  // Only update if the value is actually changing
  if (localValue.value === newValue) {
    return
  }

  // Update local value immediately
  localValue.value = newValue

  // Schedule the debounced update
  debouncedSetValue(newValue)
}

function increment() {
  if (!props.maxQuantity || localValue.value < props.maxQuantity) {
    const newValue = props.multiplier > 1
      ? localValue.value + props.multiplier
      : localValue.value + 1
    updateValue(newValue)
  }
}

function decrement() {
  if (localValue.value > 0) {
    if (props.disableDecrementOnReachMinQuantity && localValue.value === props.minQuantity) {
      return
    }
    const minValue = props.minQuantity || 0
    let newValue: number

    if (props.multiplier > 1) {
      newValue = Math.max(0, localValue.value - props.multiplier)
    }
    else if (minValue && localValue.value === minValue) {
      newValue = 0
    }
    else {
      newValue = Math.max(0, localValue.value - 1)
    }

    updateValue(newValue)
  }
}

function startEditing() {
  isEditing.value = true
  tempValue.value = displayValue.value
}

function submitEdit() {
  if (isAllowedToSubmit.value) {
    updateValue(Number(tempValue.value))
  }
  cancelEditing()
}

function handleKeydown(event: KeyboardEvent) {
  switch (event.key) {
    case 'Enter':
      submitEdit()
      break
    case 'Escape':
      cancelEditing()
      break
  }
}

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement
  if (!isEditing.value) {
    startEditing()
    tempValue.value = Number.parseInt(target.value) || 0
  }
}

function validationCheck(value: number, type: 'error' | 'hint' = 'error') {
  if (props.disableDecrementOnReachMinQuantity && value === 0) {
    return {
      isValid: false,
      message: [t('form.validation.minValue', { min: props.minQuantity || 0 })],
    }
  }
  return {
    isValid: maximumNumber(value, props.maxQuantity || null, type).isValid
      && isMultipleOf(value, props.multiplier).isValid
      && minimumNumber(value, props.minQuantity || 0).isValid,
    message: [
      isMultipleOf(value, props.multiplier, t('form.validation.multiplier', { multiplier: props.multiplier })).message,
      maximumNumber(value, props.maxQuantity || null, type, t('form.validation.maxValue', { max: props.maxQuantity })).message,
      minimumNumber(value, props.minQuantity || 0, t('form.validation.minValue', { min: props.minQuantity || 0 })).message,
    ].filter(Boolean),
  }
}

function cancelEditing() {
  isEditing.value = false
}

const sizeClasses = computed(() => ({
  'vmz-quantity-change--small': props.size === 'small',
  'vmz-quantity-change--default': props.size === 'default',
}))
</script>

<template>
  <div
    data-test-id="product-quantity-change"
    class="vmz-quantity-change"
    :class="sizeClasses"
  >
    <vmz-text-field
      v-if="!isEditing"
      :variant="props.variant"
      :model-value="displayValue"
      density="compact"
      :hide-details="validationCheck(displayValue, 'hint').isValid"
      flat
      single-line
      class="vmz-quantity-change__number"
      :class="quantityValue >= maxQuantity ? 'disabled-plus' : ''"
      type="number"
      :hint="validationCheck(displayValue, 'hint').message.join(' ')"
      :prepend-inner-icon="quantityValue <= (props?.minQuantity || 1) ? disableDecrementOnReachMinQuantity ? IconMinus : 'veticon-trash' : IconMinus"
      :append-inner-icon="IconPlus"
      :data-test-id="quantityValue <= (props?.minQuantity || 1) ? 'trash-icon' : undefined"
      @click:append-inner="increment"
      @click:prepend-inner="decrement"
      @input="handleInput"
    />
    <vmz-text-field
      v-show="isEditing"
      v-model="tempValue"
      density="compact"
      variant="outlined"
      :hide-details="validationCheck(tempValue).isValid"
      flat
      :rules="validationCheck(tempValue).message"
      single-line
      class="vmz-quantity-change__number vmz-quantity-change__editable"
      type="number"
      inputmode="numeric"
      autofocus
      @keydown="handleKeydown"
    >
      <template #prepend-inner>
        <vmz-btn
          density="compact"
          class="px-5"
          icon
          data-track-id="product_quantity_cancel_edit"
          @click="cancelEditing"
        >
          <IconClose />
        </vmz-btn>
      </template>
      <template #append-inner>
        <vmz-btn
          :disabled="!isAllowedToSubmit"
          class="px-5"
          density="compact"
          icon
          data-track-id="product_quantity_submit_edit"
          @click="submitEdit"
        >
          <IconCheck />
        </vmz-btn>
      </template>
    </vmz-text-field>
  </div>
</template>

<style scoped lang="scss">
.vmz-quantity-change {
  &__number {
    max-width: 160px;
    width: 160px;
    :deep(.v-field) {
      border-radius: 8px;
    }
    :deep(.v-field__input) {
      text-align: center;
      padding: 0 !important;
      -moz-appearance:textfield;
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
    &.disabled-plus {
      :deep(.v-field__append-inner) {
        color: rgb(var(--v-theme-vm-grey-4));
        > [role=button].v-icon {
          cursor: default !important;
        }
      }
    }
  }

  &__editable {
    :deep(.v-field) {
      padding-inline: 0 !important;
    }
  }

  // Size variants
  &--small {
    .vmz-quantity-change__number {
      max-width: 120px;
      width: 120px;
    }
  }
}
</style>
